"use client";

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Button } from "../../atoms/button/button";
import { Chip } from "../../atoms/chip/chip";
import { INERT_SLOT } from "../../atoms/inert-slot";
import { ToggleTile } from "../../atoms/toggle-tile/toggle-tile";
import { SearchFieldClient } from "../../atoms/search-field/search-field.client";
import { Tag } from "../../atoms/tag/tag";
import { Text } from "../../atoms/text/text";
import { ZipCode } from "../../molecules/zip-code/zip-code.client";
import { analyticsText, fireLinkClick, fireRecommenderStatus, fireSearch } from "../../services/analytics";
import {
  fetchBootstrap,
  edgescapeZip,
  fetchRsnFee,
  isZip,
  lookupCounties,
  lookupMarket,
  matchChannels,
  postLocale,
  postRecommend,
  readCookie,
  rsnFeeCookies,
  siteLocationCookies,
  rsnFeeFromCookies,
  zipFromCookies,
  type PostedLocale,
  type RecommenderAnswer,
  type RecommenderBootstrap,
  type RecommenderChannel,
  type RecommenderMarket,
  type ZipCounty
} from "./dynamic-recommender-api";
import {
  CLEAR_ALL_LINK_NAME,
  RECOMMENDER_LINK_POSITION,
  RECOMMENDER_SEARCH_CATEGORY,
  RECOMMENDER_SEARCH_SOURCE,
  RESET_SEARCH_LINK_NAME,
  UPDATE_LOCATION_LINK_NAME,
  cardLinkName,
  channelLineupDestination,
  clickedHref,
  interestLinkName,
  recommendationStatus,
  removeLinkName,
  searchLinkName,
  selectionStatus
} from "./dynamic-recommender-analytics";
import { cardTags, toCardSlot, type CardCopy, type CardSlot } from "./dynamic-recommender-cards";
import styles from "./dynamic-recommender.module.css";
import type { ProductSettings } from "./dynamic-recommender-products";
import { RecommenderCard, RecommenderNotIncluded } from "./recommender-card";
import { RecommenderMiniDock } from "./recommender-mini-dock.client";
import { withSafeRender } from "../../atoms/safe-render";

export interface RecommenderInterest {
  id: string;
  label: string;
}

/** Everything the client half draws, already defaulted by the server half. */
export interface DynamicRecommenderClientProps {
  headingId: string;
  interests: RecommenderInterest[];
  /** The recommender's own tiles when the page already has them, so the tiles render before it answers. */
  knownTiles?: RecommenderInterest[];
  /** The blok's product list, in authored order. */
  products: ProductSettings[];
  apiUrl: string;
  lookupUrl: string;
  copy: {
    zipPrefix: string;
    zipLoading: string;
    zipNoMarket: string;
    zipError: string;
    zipInputLabel: string;
    zipApplyLabel: string;
    countyLabel: string;
    clearAllLabel: string;
    searchHelper: string;
    searchLabel: string;
    resetSearchLabel: string;
    searchNoResults: string;
    selectedChannelsLabel: string;
    resultsHeading: string;
    empty: string;
    loading: string;
    error: string;
    noMatch: string;
    ctaLabel: string;
    /** The mini cards' one action, which scrolls to the full card. */
    miniViewLabel: string;
    card: CardCopy;
  };
}

type ZipState =
  | { status: "loading"; zip: string; county?: ZipCounty; counties?: ZipCounty[] }
  /* `guessed`: opened on a county nothing saved, so an Update saves it even unchanged. */
  | { status: "ready"; zip: string; market: RecommenderMarket; county: ZipCounty; counties: ZipCounty[]; guessed?: boolean }
  | { status: "county"; zip: string; counties: ZipCounty[] }
  | { status: "no-market"; zip: string }
  | { status: "error"; zip: string };

/** Before this, a fast answer paints no loading line at all. */
const LOADING_DELAY_MS = 150;
/** The fee is optional: past this the cards show the default market's fee rather than keep the zip waiting. */
const FEE_TIMEOUT_MS = 3000;
/** A tap is sent at once when nothing went out this recently; quicker taps wait and collapse into one request, which the API's rate limit assumes. */
const REQUEST_DEBOUNCE_MS = 250;

const titleCase = (value: string) => value.toLowerCase().replace(/(^|[\s-])\S/g, m => m.toUpperCase());

/** A slot's package and its mini packs, as the mini card and the rail name it. */
const bundleName = (slot: CardSlot) => [slot.base.name_label, ...slot.minis.map(mini => mini.name_label)].join(" + ");

/** A control that stays on the page reports the page as its destination. */
function reportClick(linkName: string, linkDestination = window.location.href) {
  fireLinkClick({
    linkName,
    linkPosition: RECOMMENDER_LINK_POSITION,
    linkDestination
  });
}

/** The button a delegated click landed on, if it carries a value. */
function clickedValue(event: React.MouseEvent, selector: string): string | null {
  const target = (event.target as HTMLElement).closest<HTMLButtonElement>(selector);
  return target && !target.disabled ? target.value : null;
}

function DynamicRecommenderClientImpl({
  headingId,
  interests,
  knownTiles,
  products,
  apiUrl,
  lookupUrl,
  copy
}: DynamicRecommenderClientProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const listboxId = `${uid}-channels`;
  const zipId = `${uid}-zip`;
  const resultsId = `${uid}-results`;

  const [boot, setBoot] = useState<RecommenderBootstrap | null>(null);
  const [bootFailed, setBootFailed] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [picks, setPicks] = useState<RecommenderChannel[]>([]);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [listOpen, setListOpen] = useState(false);
  const [zip, setZip] = useState<ZipState>({
    status: "loading",
    zip: ""
  });
  const [locale, setLocale] = useState<PostedLocale | null>(null);
  /* Null until known: the engine then shows the default market's fee. */
  const [rsnFee, setRsnFee] = useState<number | null>(null);
  /* The market's own list once known: it adds local stations and RSNs and drops what is not sold here. */
  const [marketChannels, setMarketChannels] = useState<RecommenderChannel[] | null>(null);
  const [zipPanelOpen, setZipPanelOpen] = useState(false);
  const [zipDraft, setZipDraft] = useState("");
  const [zipShapeError, setZipShapeError] = useState(false);
  /* A full ZIP is looked up as it is typed, so its counties are offered before Update; Update reuses the same lookup. */
  const draftLookup = useRef<{ zip: string; counties: Promise<ZipCounty[]> } | null>(null);
  /* An Update waiting on that lookup; any edit to the field cancels it. */
  const pendingUpdate = useRef<Promise<ZipCounty[]> | null>(null);
  const [draftCounties, setDraftCounties] = useState<{ zip: string; counties: ZipCounty[] } | null>(null);
  /* A picked county waits for Update, as in the DDS zip module. */
  const [pickedCounty, setPickedCounty] = useState<{ zip: string; index: number } | null>(null);
  const [countyMissing, setCountyMissing] = useState(false);
  const [answer, setAnswer] = useState<RecommenderAnswer | null>(null);
  const [answeredProductsKey, setAnsweredProductsKey] = useState("");
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const zipRun = useRef(0);
  /* Bumped whenever the shopper works the ZIP panel, so an Update that lands closes it only if left alone since. */
  const panelTouch = useRef(0);
  const lastSent = useRef(Number.NEGATIVE_INFINITY);
  /* Each zip's fee is asked once a visit; a failed ask is dropped so the next visit to that zip tries again. */
  const feeLoads = useRef(new Map<string, Promise<number | null>>());
  /* Bumped by each shopper action; the status events report once, when the answer to the last one settles. */
  const [reportTick, setReportTick] = useState(0);
  const reportedTick = useRef(0);
  const requestRun = useRef(0);
  const [answeredRun, setAnsweredRun] = useState(0);
  const reportAfterSettle = () => setReportTick(tick => tick + 1);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  /* The block's list sets which tiles show and their order; the engine's settings name them.
     Ids the engine does not know are dropped, so a stale id cannot ask for an interest nobody scores. */
  const tiles = useMemo(() => {
    const named = boot ? boot.tiles : knownTiles;
    if (!named || named.length === 0) {
      return interests;
    }
    const labels = new Map(named.map(tile => [tile.id, tile.label]));
    const authored = interests.filter(tile => labels.has(tile.id)).map(tile => ({
      ...tile,
      label: labels.get(tile.id) as string
    }));
    return authored.length > 0 ? authored : named.map(({ id, label }) => ({
      id,
      label
    }));
  }, [boot, knownTiles, interests]);

  const suggestions = useMemo(
    () => (boot ? matchChannels(marketChannels || boot.channels, query) : []),
    [boot, marketChannels, query]
  );
  /* A new list (a narrowed market, a new query) drops the highlight, so the
     active descendant never names an option that is no longer rendered. */
  const [highlightedIn, setHighlightedIn] = useState(suggestions);
  if (highlightedIn !== suggestions) {
    setHighlightedIn(suggestions);
    setActiveIndex(-1);
  }

  /* The site's cookie answers when it holds this zip; otherwise the site's own offers call does, once. */
  const feeFor = useCallback((nextZip: string) => {
    const known = feeLoads.current.get(nextZip);
    if (known) {
      return known;
    }
    const cookieFee = rsnFeeFromCookies(document.cookie, nextZip);
    const controller = new AbortController();
    const timer = cookieFee === null ? setTimeout(() => controller.abort(), FEE_TIMEOUT_MS) : undefined;
    const load = cookieFee !== null ?
      Promise.resolve(cookieFee) :
      fetchRsnFee(nextZip, controller.signal)
        .catch(() => {
          feeLoads.current.delete(nextZip);
          return null;
        })
        .finally(() => clearTimeout(timer));
    feeLoads.current.set(nextZip, load);
    return load;
  }, []);

  /* `counties` is the zip's list `county` was picked from: it stays offered once applied, so another can be picked.
     `applied` is the shopper's own Update, which moves the site's zip with it; opening the page never does. */
  const resolveZip = useCallback(async (nextZip: string, county?: ZipCounty, counties?: ZipCounty[], applied = false) => {
    zipRun.current += 1;
    const run = zipRun.current;
    const touch = panelTouch.current;
    const current = () => run === zipRun.current;
    /* Started first so it runs beside the lookups rather than after them. */
    const feeLoad = feeFor(nextZip);
    setPickedCounty(null);
    setZip({
      status: "loading",
      zip: nextZip,
      ...(county ? {
        county,
        counties
      } : null)
    });
    /* The last market's stations are not on sale here; search the national list until this one answers. */
    setMarketChannels(null);
    try {
      let chosen = county;
      let listed = counties || (county ? [county] : []);
      let guessed = false;
      if (!chosen) {
        const pending = draftLookup.current?.zip === nextZip ? draftLookup.current.counties : null;
        draftLookup.current = null;
        const counties = await (pending || lookupCounties(lookupUrl, nextZip));
        if (!current()) {
          return;
        }
        if (counties.length === 0) {
          setLocale(null);
          setZip({
            status: "no-market",
            zip: nextZip
          });
          return;
        }
        /* The county the site last saved, as its zip module finds it: by its county key, else, for the site's own
           zip, by its DMA cookies. Opening the page saves nothing, so it never asks: with none saved it shows one
           in the cookies' DMA, else the first, as the zip module shows its first. Only an Update asks. */
        const savedKey = readCookie(document.cookie, "currentCounty");
        const byKey = savedKey ? counties.filter(item => item.key === savedKey) : [];
        const siteOwn = readCookie(document.cookie, "currentZipcode") === nextZip;
        const nielsenDma = siteOwn ? readCookie(document.cookie, "currentNielsenDma") : "";
        const dtvDma = siteOwn ? readCookie(document.cookie, "currentDma") : "";
        const byDma = nielsenDma ? counties.filter(item => item.nielsenDma === nielsenDma && (!dtvDma || item.dtvDma === dtvDma)) : [];
        const saved = byKey.length === 1 ? byKey : byDma;
        if (applied && counties.length > 1 && saved.length !== 1) {
          setLocale(null);
          setZip({
            status: "county",
            zip: nextZip,
            counties
          });
          setZipPanelOpen(true);
          return;
        }
        guessed = counties.length > 1 && saved.length !== 1;
        [chosen] = saved.length === 1 ? saved : byDma.length ? byDma : counties;
        listed = counties;
      }
      const market = await lookupMarket(lookupUrl, nextZip, chosen.nielsenDma);
      const posted: PostedLocale = {
        zip: nextZip,
        county: chosen.raw,
        market
      };
      const [resolved, fee] = await Promise.all([postLocale(apiUrl, posted), feeLoad]);
      if (!current()) {
        return;
      }
      /* An applied zip becomes the site's, as its zip module saves one. Otherwise only the site's own zip, or the
         edge's when the site has none yet, gets a missing fee filled. */
      const siteZip = readCookie(document.cookie, "currentZipcode");
      const ownZip = siteZip === "" ? edgescapeZip(document.cookie) : siteZip;
      let cookies: string[] = [];
      if (applied) {
        cookies = siteLocationCookies(nextZip, chosen, fee, window.location.hostname);
      } else if (fee !== null && ownZip === nextZip && rsnFeeFromCookies(document.cookie, nextZip) !== fee) {
        cookies = rsnFeeCookies(nextZip, fee, window.location.hostname);
      }
      cookies.forEach(cookie => {
        document.cookie = cookie;
      });
      const sold = new Set(resolved.channels.map(channel => channel[0]));
      setLocale(posted);
      setRsnFee(fee);
      setMarketChannels(resolved.channels);
      /* A station the shopper can no longer find must not keep scoring. */
      setPicks(prev => prev.filter(channel => sold.has(channel[0])));
      setZip({
        status: "ready",
        zip: nextZip,
        market: resolved.market,
        county: chosen,
        counties: listed,
        guessed
      });
      /* An Update that lands closes the panel, as the site's zip module does; one that fails keeps it open. */
      if (applied && touch === panelTouch.current) {
        setZipPanelOpen(false);
      }
    } catch {
      if (current()) {
        setLocale(null);
        setZip({
          status: "error",
          zip: nextZip
        });
      }
    }
  }, [apiUrl, feeFor, lookupUrl]);

  useEffect(() => {
    const controller = new AbortController();
    fetchBootstrap(apiUrl, controller.signal)
      .then(setBoot)
      .catch(() => {
        if (!controller.signal.aborted) {
          setBootFailed(true);
        }
      });
    const startZip = zipFromCookies(document.cookie);
    setZipDraft(startZip);
    void resolveZip(startZip);
    return () => controller.abort();
  }, [apiUrl, resolveZip]);

  /* Keyed on content: the server half builds a new list on every render, and
     a new identity would re-post the shopper's picks. */
  const productsKey = JSON.stringify(products.map(item => [item.code, item.callSigns]));
  const postedProducts = useMemo(() => (JSON.parse(productsKey) as [string, string[]][]).map(([code, channels]) => ({
    code,
    channels
  })), [productsKey]);

  const selectionCount = selected.length + picks.length;
  /* The engine scores a market only; without one there is nothing to ask. */
  const zipBlocked = zip.status !== "ready";

  useEffect(() => {
    requestRun.current += 1;
    const run = requestRun.current;
    if (selectionCount === 0 || !boot || zipBlocked || !locale) {
      setAnswer(null);
      setFailed(false);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const wait = Date.now() - lastSent.current >= REQUEST_DEBOUNCE_MS ? 0 : REQUEST_DEBOUNCE_MS;
    const timer = setTimeout(() => setLoading(true), LOADING_DELAY_MS);
    const send = setTimeout(() => {
      lastSent.current = Date.now();
      postRecommend(apiUrl, {
        tileIds: selected,
        channelIds: picks.map(channel => channel[0]),
        locale,
        rsnFee,
        products: postedProducts
      }, controller.signal)
        .then(next => {
          setAnswer(next);
          setAnsweredRun(run);
          setAnsweredProductsKey(productsKey);
          setFailed(false);
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setAnswer(null);
            setFailed(true);
          }
        })
        .finally(() => {
          clearTimeout(timer);
          if (!controller.signal.aborted) {
            setLoading(false);
          }
        });
    }, wait);
    return () => {
      clearTimeout(timer);
      clearTimeout(send);
      controller.abort();
    };
  }, [apiUrl, boot, locale, picks, postedProducts, productsKey, rsnFee, selected, selectionCount, zipBlocked]);

  /* Declared after the request so a change it starts is seen here as unanswered. Only a zip the engine can
     score reports: a failed answer, a failed zip or one with no market reports nothing until the next settle. */
  useEffect(() => {
    if (reportTick === reportedTick.current || zip.status !== "ready") {
      return;
    }
    const asks = selectionCount > 0 && !zipBlocked;
    if (asks && (!boot || !locale || answeredRun !== requestRun.current)) {
      return;
    }
    reportedTick.current = reportTick;
    const labels = new Map(tiles.map(tile => [tile.id, tile.label]));
    fireRecommenderStatus({
      eventName: "recommender_selection_status",
      ...selectionStatus(selected.map(id => labels.get(id) || id), picks.map(channel => channel[3]), zip.zip)
    });
    if (asks) {
      recommendationStatus(answer?.cards ?? null).forEach(dimensions => fireRecommenderStatus({
        eventName: "recommender_recommendation_status",
        ...dimensions
      }));
    }
  }, [answer, answeredRun, boot, locale, picks, reportTick, selected, selectionCount, tiles, zip, zipBlocked]);

  const onGridClick = (event: React.MouseEvent) => {
    const id = clickedValue(event, "button[value]");
    if (id) {
      reportClick(interestLinkName(tiles.find(tile => tile.id === id)?.label || id, !selected.includes(id)));
      setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
      reportAfterSettle();
    }
  };

  const pick = (channel: RecommenderChannel) => {
    fireSearch({
      searchTerm: analyticsText(channel[3]),
      searchCategory: RECOMMENDER_SEARCH_CATEGORY,
      searchSource: RECOMMENDER_SEARCH_SOURCE
    });
    reportClick(searchLinkName(channel[3]));
    if (!picks.some(x => x[0] === channel[0])) {
      reportAfterSettle();
    }
    setPicks(prev => (prev.some(x => x[0] === channel[0]) ? prev : [...prev, channel]));
    setQuery("");
    setActiveIndex(-1);
    setListOpen(false);
  };

  const onChipsClick = (event: React.MouseEvent) => {
    const value = clickedValue(event, "button[value]");
    if (value !== null) {
      const removed = picks.find(channel => channel[0] === value);
      if (removed) {
        reportClick(removeLinkName(removed[3]));
        reportAfterSettle();
      }
      setPicks(prev => prev.filter(channel => channel[0] !== value));
      /* The pressed button is about to unmount; focus goes back to the search it came from. */
      comboboxRef.current?.querySelector("input")?.focus();
    }
  };

  const clearAll = () => {
    reportClick(CLEAR_ALL_LINK_NAME);
    if (selectionCount > 0) {
      reportAfterSettle();
    }
    setSelected([]);
    setPicks([]);
    setQuery("");
    setListOpen(false);
  };

  const resetSearch = () => {
    reportClick(RESET_SEARCH_LINK_NAME);
    if (picks.length > 0 || query !== "") {
      reportAfterSettle();
    }
    setPicks([]);
    setQuery("");
    setListOpen(false);
  };

  const showList = listOpen && query.trim().length >= 2;

  /* Focus stays in the field, so the browser never scrolls the list for an arrow key. */
  useEffect(() => {
    if (showList && activeIndex >= 0) {
      document.getElementById(`${listboxId}-${activeIndex}`)?.scrollIntoView?.({ block: "nearest" });
    }
  }, [activeIndex, listboxId, showList]);

  /* Bound on the wrapper: key events bubble from the field, so the atom takes no handler prop. */
  const onSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setListOpen(false);
      setActiveIndex(-1);
      return;
    }
    /* A list closed by Escape or a blur comes back on an arrow key, the APG combobox pattern. */
    if (!listOpen && query.trim().length >= 2 && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      setListOpen(true);
      return;
    }
    if (!showList || suggestions.length === 0) {
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const span = suggestions.length + 1;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex(((activeIndex + 1 + step + span) % span) - 1);
      return;
    }
    /* With nothing highlighted, Enter falls through to the form's submit. */
    const choice = event.key === "Enter" ? suggestions[activeIndex] : undefined;
    if (choice) {
      event.preventDefault();
      pick(choice);
    }
  };

  /* A zip with several counties needs one picked before Update applies it. */
  const changeZipDraft = (value: string) => {
    panelTouch.current += 1;
    pendingUpdate.current = null;
    setZipShapeError(false);
    setCountyMissing(false);
    setZipDraft(value);
    if (!isZip(value) || value === zip.zip || draftLookup.current?.zip === value) {
      return;
    }
    const counties = lookupCounties(lookupUrl, value);
    draftLookup.current = {
      zip: value,
      counties
    };
    counties.then(list => {
      if (draftLookup.current?.counties === counties) {
        setDraftCounties({
          zip: value,
          counties: list
        });
      }
    }, () => {
      // A failed lookup is not reused: Update then asks again.
      if (draftLookup.current?.counties === counties) {
        draftLookup.current = null;
      }
    });
  };
  /* Reported when Update moves the zip or its county, or first saves the one shown; not on every press. */
  const applyLocation = (draft: string, county?: ZipCounty, counties?: ZipCounty[]) => {
    reportClick(UPDATE_LOCATION_LINK_NAME);
    reportAfterSettle();
    void resolveZip(draft, county, counties, true);
  };
  const applyZip = (draft: string) => {
    if (!isZip(draft)) {
      setZipShapeError(true);
      return;
    }
    /* The zip already applied, or being applied: Update only moves it to another of its counties. */
    const shown = draft === zip.zip && (zip.status === "ready" || zip.status === "loading") ? zip : null;
    const listed = draft !== zip.zip ? (draftCounties?.zip === draft ? draftCounties.counties : null) :
      zip.status === "county" ? zip.counties : shown?.counties || null;
    /* Update before the typed ZIP's counties arrive waits for them, so the answer does not depend on timing. */
    const pending = draft !== zip.zip && !listed && draftLookup.current?.zip === draft ? draftLookup.current.counties : null;
    if (pending) {
      pendingUpdate.current = pending;
      pending.then(counties => {
        if (pendingUpdate.current !== pending) {
          return;
        }
        pendingUpdate.current = null;
        if (counties.length > 1) {
          setCountyMissing(true);
        } else {
          applyLocation(draft, counties[0], counties.length ? counties : undefined);
        }
      }, () => {
        // The failed lookup was dropped as it failed, so Update asks again.
        if (pendingUpdate.current === pending) {
          pendingUpdate.current = null;
          applyLocation(draft);
        }
      });
      return;
    }
    if (listed && listed.length > 1) {
      const guessed = shown?.status === "ready" && shown.guessed;
      const county = pickedCounty?.zip === draft ? listed[pickedCounty.index] : guessed ? shown.county : undefined;
      if (county && (county !== shown?.county || guessed)) {
        applyLocation(draft, county, listed);
      } else if (!county && !shown) {
        setCountyMissing(true);
      }
      return;
    }
    if (!shown) {
      applyLocation(draft, listed?.length === 1 ? listed[0] : undefined, listed || undefined);
    }
  };
  /* ZipCode's Update is a plain button, not a form submit, so Enter in the ZIP field submits here. */
  const submitZipOnEnter = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.repeat && (event.target as HTMLElement).id === `${zipId}-zip`) {
      event.preventDefault();
      applyZip(zipDraft);
    }
  };

  const [zipLabel, zipLocation] = ((): [string, string] => {
    if (zip.status === "ready") {
      const { county } = zip.market;
      return [copy.zipPrefix, `${zip.zip}, ${titleCase(county.name)}, ${county.state}`];
    }
    if (zip.status === "no-market") {
      return [copy.zipNoMarket, zip.zip];
    }
    if (zip.status === "error") {
      return [copy.zipError, zip.zip];
    }
    if (zip.status === "county") {
      return [copy.zipPrefix, zip.zip];
    }
    if (zip.county) {
      return [copy.zipPrefix, `${zip.zip}, ${titleCase(zip.county.name)}, ${zip.county.state}`];
    }
    return ["", copy.zipLoading];
  })();
  /* Counties belong to the zip they were looked up for; the county being applied, then the market's own, is shown locked. */
  const editingOtherZip = zipDraft !== zip.zip;
  const draftListed = editingOtherZip && draftCounties?.zip === zipDraft ? draftCounties.counties : null;
  const countyOption = (county: ZipCounty, index: number) => ({
    value: String(index),
    label: `${titleCase(county.name)}, ${county.state}`
  });
  const shownZip = zip.status === "ready" || zip.status === "loading" ? zip : null;
  const shownList = shownZip?.counties && shownZip.counties.length > 1 ? shownZip.counties : null;
  const countyOptions = editingOtherZip ? (draftListed || []).map(countyOption) :
    zip.status === "county" ? zip.counties.map(countyOption) :
      shownList ? shownList.map(countyOption) :
        zip.status === "ready" ? [{
          value: "market",
          label: `${titleCase(zip.market.county.name)}, ${zip.market.county.state}`
        }] :
          zip.status === "loading" && zip.county ? [{
            value: "market",
            label: `${titleCase(zip.county.name)}, ${zip.county.state}`
          }] :
            [];
  const picked = pickedCounty?.zip === zipDraft ? String(pickedCounty.index) : "";

  const slots: CardSlot[] = [];
  const cards = answer?.cards;
  /* The rails stay put between answers; only a slot whose package or add-ons changed redraws its cards. */
  const slotKeys: string[] = [];
  if (cards) {
    const tags = cardTags(cards.mode, cards.alternativeKind);
    /* An answer's item indexes point into the list it was asked with; until the
       new list's answer lands, an edited list would hand cards another item's links. */
    const answeredProducts = answeredProductsKey === productsKey ? products : [];
    const top = toCardSlot(cards.top, tags.top, copy.card, `${uid}-top`, answeredProducts);
    const alternative = cards.alternative ?
      toCardSlot(cards.alternative, tags.alternative, copy.card, `${uid}-alternative`, answeredProducts) :
      null;
    [cards.top, cards.alternative].forEach((card, index) => {
      const slot = index === 0 ? top : alternative;
      if (card && slot) {
        slots.push(slot);
        slotKeys.push([card.code, ...(card.requiredAddons || []).map(addon => addon.name)].join("+"));
      }
    });
  }

  /* Cards draw only after an answer, so on the client: the page's address is there to read. */
  const cardReporting = (moreLabel?: string) => ({
    link_position: RECOMMENDER_LINK_POSITION,
    more_link_name: analyticsText(moreLabel),
    more_link_destination: moreLabel ? channelLineupDestination() : undefined
  });

  const statusText = failed || bootFailed || (zip.status === "error" && selectionCount > 0) ? copy.error :
    loading ? copy.loading :
      selectionCount === 0 ? copy.empty :
        answer && !cards ? copy.noMatch : "";

  return (
    <>
      <div className={styles.bar} onKeyDown={submitZipOnEnter}>
        <ZipCode
          addLocationCtaLabel={copy.zipApplyLabel}
          apiErrorMessage={zip.status === "error" && !editingOtherZip ? `${copy.zipError} ${zip.zip}` : undefined}
          countyLabel={copy.countyLabel}
          counties={countyOptions}
          countyPlaceholder={copy.countyLabel}
          ctaLabel={copy.zipApplyLabel}
          expanded={zipPanelOpen}
          id={zipId}
          inputValue={zipDraft}
          locationText={zipLocation}
          countySelectionError={countyMissing}
          ctaLinkAttributes={false}
          onClear={() => {
            pendingUpdate.current = null;
            setZipDraft("");
            setZipShapeError(false);
            setCountyMissing(false);
          }}
          onCountySelect={value => {
            panelTouch.current += 1;
            setPickedCounty({
              zip: zipDraft,
              index: Number(value)
            });
            setCountyMissing(false);
          }}
          onSubmit={applyZip}
          onToggle={open => {
            panelTouch.current += 1;
            setZipPanelOpen(open);
          }}
          onZipChange={changeZipDraft}
          selectedCounty={editingOtherZip ? (draftListed?.length === 1 ? "0" : picked) :
            zip.status === "county" ? picked :
              shownList ? picked || String(shownList.indexOf(shownZip?.county as ZipCounty)) :
                countyOptions.length ? "market" : ""}
          triggerLabel={zipLabel}
          validateOnSubmit={false}
          zip={zip.zip}
          zipInputError={zipShapeError}
          zipLabel={copy.zipInputLabel}
          zipLookupErrorMessage={`${copy.zipNoMarket} ${editingOtherZip ? zipDraft : zip.zip}`}
          zipNoResults={editingOtherZip ? draftListed?.length === 0 : zip.status === "no-market"}
        />
        <button className={styles.clearAll} onClick={clearAll} type="button">
          <svg aria-hidden="true" className={styles.clearGlyph} focusable="false" viewBox="0 0 16 16">
            <path d="M13 8a5 5 0 1 1-1.46-3.54M13 3v2.5h-2.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" />
          </svg>
          <Text as="span" variant="body-3" weight="medium">{copy.clearAllLabel}</Text>
        </button>
      </div>

      {/* One listener for every tile: the atoms take no handler prop. */}
      <div aria-labelledby={headingId} className={styles.grid} onClick={onGridClick} role="group">
        {tiles.map(tile => (
          <ToggleTile key={tile.id} label={tile.label} selected={selected.includes(tile.id)} value={tile.id} />
        ))}
      </div>

      <div className={styles.search}>
        <Text as="p" className={styles.searchHelper} tone="tertiary" variant="subhead-2" weight="medium">{copy.searchHelper}</Text>
        <div className={styles.searchColumn}>
          <button className={styles.resetSearch} onClick={resetSearch} type="button">
            {/* Not Text: its tone colour would paint over the link colour. */}
            <span className="dds-text dds-body-3 dds-weight-medium">{copy.resetSearchLabel}</span>
          </button>
          {/* Keys bubble here from the field; every control inside is a real one. The list
              overlays the chips and cards, so it closes once focus leaves the field. */}
          <div
            className={styles.combobox}
            onBlur={event => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                setListOpen(false);
                setActiveIndex(-1);
              }
            }}
            onKeyDown={onSearchKeyDown}
            ref={comboboxRef}
          >
            <SearchFieldClient
              ariaActiveDescendant={showList && activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined}
              ariaAutocomplete="list"
              ariaControls={listboxId}
              ariaExpanded={showList}
              ariaLabel={copy.searchLabel}
              disabled={!boot}
              icon
              id={`${uid}-search`}
              label={copy.searchLabel}
              onSubmit={() => suggestions[0] && pick(suggestions[0])}
              onValueChange={next => {
                setQuery(next);
                setActiveIndex(-1);
                setListOpen(true);
              }}
              value={query}
            />
            {/* A press anywhere in the list, padding and scrollbar included, keeps focus in the field.
                tabIndex -1: Chrome makes a scrolling box a tab stop, and Tab must leave the combobox. */}
            <ul
              className={styles.listbox}
              hidden={!showList}
              id={listboxId}
              onMouseDown={event => event.preventDefault()}
              role="listbox"
              tabIndex={-1}
            >
              {suggestions.map((channel, index) => (
                /* Options are not tab stops: the field keeps focus and
                   aria-activedescendant points here, the APG combobox pattern. */

                <li
                  aria-selected={index === activeIndex}
                  className={styles.option}
                  id={`${listboxId}-${index}`}
                  key={channel[0]}
                  onClick={() => pick(channel)}
                  role="option"
                >
                  {/* Plain text, not Text: a catalog name is data, never markup to parse. */}
                  <span className="dds-text dds-body-3">{channel[3]}</span>
                  <Text as="span" className={styles.optionMeta} variant="label">
                    {`ch ${Number(channel[1]) || channel[1]}`}
                  </Text>
                </li>
              ))}
              {showList && suggestions.length === 0 && (
                <li aria-disabled="true" className={styles.option} role="option" aria-selected={false}>
                  <Text as="span" variant="body-2">{copy.searchNoResults}</Text>
                </li>
              )}
            </ul>
          </div>
        </div>
        {/* Its own row under the field, as in the frame, so the helper stays level with the field. */}
        {picks.length > 0 && (
          <ul aria-label={copy.selectedChannelsLabel} className={styles.chips} onClick={onChipsClick}>
            {picks.map(channel => (
              <li key={channel[0]}>
                <Chip label={channel[3]} value={channel[0]} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <section
        aria-labelledby={selectionCount > 0 ? `${resultsId}-heading` : undefined}
        className={styles.results}
      >
        {selectionCount > 0 && (
          <Text as="h3" id={`${resultsId}-heading`} variant="headline-3">
            {copy.resultsHeading}
          </Text>
        )}
        {/* Always in the DOM so a live region exists before the first change. Over cards it is
            read but not drawn: the dimmed cards say "loading", and a line above them would push them down. */}
        <div aria-live="polite" className={slots.length > 0 ? `${styles.status} dds-sr-only` : styles.status} role="status">
          {statusText && <Text as="p" variant="body-2">{statusText}</Text>}
        </div>
        {/* The last answer stays while the next one loads, so the section never collapses between taps. */}
        {slots.length > 0 && (
          <div
            className={styles.cards}
            ref={cardsRef}
            data-stale={loading || undefined}
            /* pointer-events stops the mouse only; inert keeps keyboard users off the stale CTAs too. */
            {...(loading ? INERT_SLOT : {})}
            data-alternative-kind={cards?.alternativeKind || undefined}
            data-card-count={cards?.count}
          >
            {slots.map((slot, index) => (
              /* Focusable from script only: a mini card's View lands here, so it carries the name View announced. */
              <div
                aria-label={[slot.tag, bundleName(slot)].filter(Boolean).join(": ")}
                className={styles.rail}
                key={slot.base.id}
                role="group"
                tabIndex={-1}
              >
                <React.Fragment key={slotKeys[index]}>
                  {slot.tag && <Tag className={styles.tag} color="yellow">{slot.tag}</Tag>}
                  <RecommenderCard
                    {...slot.base}
                    {...cardReporting(slot.base.more_label)}
                    cta_href={slot.minis.length > 0 ? undefined : slot.ctaHref}
                    cta_label={slot.minis.length > 0 || !slot.ctaHref ? undefined : copy.ctaLabel}
                    cta_link_name={cardLinkName(index, bundleName(slot))}
                  />
                  {slot.minis.map(mini => (
                    <React.Fragment key={mini.id}>
                      <span aria-hidden="true" className={styles.plus}>+</span>
                      <RecommenderCard {...mini} {...cardReporting(mini.more_label)} />
                    </React.Fragment>
                  ))}
                  {slot.minis.length > 0 && copy.ctaLabel && slot.ctaHref && (
                    <div className={styles.bundleCta}>
                      {slot.subtotal && <Text as="p" className={styles.subtotal} variant="body-3">{slot.subtotal}</Text>}
                      <Button
                        fullWidth
                        href={slot.ctaHref}
                        onClick={event => reportClick(cardLinkName(index, bundleName(slot)), clickedHref(event))}
                        size="medium"
                        variant="primary"
                      >
                        {copy.ctaLabel}
                        <span className="dds-sr-only"> {[slot.base.name_label, ...slot.minis.map(m => m.name_label)].join(" + ")}</span>
                      </Button>
                    </div>
                  )}
                  <RecommenderNotIncluded cue={copy.card.notIncludedLabel} items={slot.notIncluded} />
                </React.Fragment>
              </div>
            ))}
          </div>
        )}
      </section>
      <RecommenderMiniDock
        items={slots.map((slot, index) => ({
          packageKey: slotKeys[index],
          tag: slot.tag,
          name: bundleName(slot),
          priceDollars: slot.total.price_dollars,
          priceCents: slot.total.price_cents,
          legal: slot.total.legal_content
        }))}
        railsRef={cardsRef}
        stale={loading}
        viewLabel={copy.miniViewLabel}
      />
    </>
  );
}
export const DynamicRecommenderClient = withSafeRender(DynamicRecommenderClientImpl, "DynamicRecommenderClient");
